import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Switch,
  Button,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  useTheme,
  useMediaQuery,
  alpha,
  Fade,
  Avatar,
} from "@mui/material";
import { 
  Close as CloseIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import Category from "../../models/finance/Category";
import { CategoryType } from "../../models/finance/CategoryType";
import { validateCategoryName } from "../../utils/categoryValidation";

interface Props {
  open: boolean;
  category: Category;
  categories: Category[];
  categoryType: CategoryType;
  onClose: () => void;
  onDelete: (category: Category, fallbackId?: string) => void;
  onUpdate: (
    oldCategory: Category,
    newTitle?: string,
    newIsActive?: boolean
  ) => void;
  getFinanceItemsNumber: (
    categoryType: CategoryType,
    categoryId: string
  ) => Promise<number>;
}

const CategoryEditDialogComponent: React.FC<Props> = ({
  open,
  category,
  categories,
  categoryType,
  onClose,
  onDelete,
  onUpdate,
  getFinanceItemsNumber,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const ARCHIVED_SENTINEL = "__ARCHIVED__";
  const MAX_LENGTH = 30;
  const [editName, setEditName] = useState(category.title);
  const [editActive, setEditActive] = useState(category.isActive);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [fallbackCategoryId, setFallbackCategoryId] = useState<string>("");
  const [itemCount, setItemCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    const trimmedName = editName.trim();
    const error = validateCategoryName(trimmedName, MAX_LENGTH);
    if (error) return;

    const newName = trimmedName !== category.title ? trimmedName : undefined;
    const newIsActive = editActive !== category.isActive ? editActive : undefined;

    setLoading(true);
    try {
      onUpdate(category, newName, newIsActive);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    const fallback =
      fallbackCategoryId && fallbackCategoryId !== ARCHIVED_SENTINEL
        ? fallbackCategoryId
        : undefined;
    setLoading(true);
    try {
      onDelete(category, fallback);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (confirmDelete && itemCount === null) {
      getFinanceItemsNumber(categoryType, category.id)
        .then((count) => setItemCount(count))
        .catch(() => setItemCount(0));
    }
  }, [confirmDelete, categoryType, category.id, getFinanceItemsNumber, itemCount]);

  // Reset form when category changes
  useEffect(() => {
    setEditName(category.title);
    setEditActive(category.isActive);
    setConfirmDelete(false);
    setFallbackCategoryId("");
    setItemCount(null);
  }, [category]);

  // Default the replacement selection to Archived when entering delete confirmation
  useEffect(() => {
    if (confirmDelete) {
      setFallbackCategoryId(ARCHIVED_SENTINEL);
    }
  }, [confirmDelete]);

  const fallbackOptions = categories.filter((cat) => cat.id !== category.id && cat.isActive);
  const isExpense = categoryType === CategoryType.Expense;
  const themeColor = isExpense ? theme.palette.error : theme.palette.success;
  const hasChanges = editName.trim() !== category.title || editActive !== category.isActive;
  const isNameValid = editName.trim().length > 0 && editName.trim().length <= MAX_LENGTH;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="sm"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        }
      }}
    >
      <Fade in={open} timeout={300}>
        <Box>
          {/* Header */}
          <DialogTitle
            sx={{
              background: `linear-gradient(135deg, ${themeColor.light}20 0%, ${themeColor.main}20 100%)`,
              borderBottom: `1px solid ${theme.palette.divider}`,
              p: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  background: `linear-gradient(135deg, ${themeColor.main}, ${themeColor.dark})`,
                }}
              >
                <CategoryIcon sx={{ color: 'white' }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: themeColor.main }}>
                  Edit {isExpense ? "Expense" : "Income"} Category
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Modify category details and settings
                </Typography>
              </Box>
              <IconButton
                onClick={onClose}
                sx={{
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.text.primary, 0.08),
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 3, pt: 4 }}>
            {!confirmDelete ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {/* Category Name */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                    Category Details
                  </Typography>
                  <TextField
                    autoFocus
                    label="Category Name"
                    value={editName}
                    onChange={(e) => {
                      if (e.target.value.length <= MAX_LENGTH) {
                        setEditName(e.target.value);
                      }
                    }}
                    fullWidth
                    variant="outlined"
                    helperText={`${editName.length}/${MAX_LENGTH} characters`}
                    inputProps={{ maxLength: MAX_LENGTH }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: themeColor.main,
                          },
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: themeColor.main,
                            borderWidth: 2,
                          },
                        },
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.75rem',
                        color: editName.length > MAX_LENGTH * 0.8
                          ? theme.palette.warning.main
                          : theme.palette.text.secondary,
                      },
                    }}
                  />
                </Box>

                                                 {/* Status Toggle */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                    Category Status
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: alpha(themeColor.main, 0.04),
                    border: `1px solid ${alpha(themeColor.main, 0.12)}`,
                  }}>
                    <Switch
                      checked={editActive}
                      onChange={(e) => setEditActive(e.target.checked)}
                      sx={{
                        mt: 0.5,
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: themeColor.main,
                          '&:hover': {
                            backgroundColor: alpha(themeColor.main, 0.08),
                          },
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: themeColor.main,
                        },
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5, color: themeColor.main }}>
                        {editActive ? "Active Category" : "Inactive Category"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        {editActive 
                          ? "This category is active and can be used for new transactions"
                          : "This category is inactive and hidden from transaction selection"
                        }
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {hasChanges && (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    You have unsaved changes. Click "Save Changes" to apply them.
                  </Alert>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Confirm Category Deletion
                  </Typography>
                  <Typography variant="body2">
                    Are you sure you want to delete "<strong>{category.title}</strong>"?
                  </Typography>
                </Alert>

                {itemCount !== null && itemCount > 0 && (
                  <Box>
                    <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                      This category has {itemCount} associated transaction{itemCount !== 1 ? 's' : ''}.
                      Choose where to move them: keep the default "Archived" destination or select a replacement category.
                    </Typography>
                    
                    <FormControl fullWidth sx={{ mt: 2 }}>
                      <InputLabel>Select Destination</InputLabel>
                      <Select
                        value={fallbackCategoryId}
                        label="Select Destination"
                        onChange={(e) => setFallbackCategoryId(e.target.value)}
                        sx={{
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: themeColor.main,
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: themeColor.main,
                          },
                        }}
                      >
                        <MenuItem value={ARCHIVED_SENTINEL}>Archived (default)</MenuItem>
                        {fallbackOptions.map((cat) => (
                          <MenuItem key={cat.id} value={cat.id}>
                            {cat.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {fallbackOptions.length === 0 && (
                      <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                        No other active categories available. Items will be moved to "Archived".
                      </Alert>
                    )}
                  </Box>
                )}

                {itemCount === 0 && (
                  <Alert severity="success" sx={{ borderRadius: 2 }}>
                    This category has no associated transactions and can be safely deleted.
                  </Alert>
                )}
              </Box>
            )}
          </DialogContent>

          <Divider />

          <DialogActions sx={{ p: 3, gap: 2 }}>
            {!confirmDelete ? (
              <>
                <Button
                  onClick={onClose}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    fontWeight: 500,
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      borderColor: theme.palette.text.primary,
                      backgroundColor: alpha(theme.palette.text.primary, 0.04),
                    },
                  }}
                >
                  Cancel
                </Button>
                
                <Button
                  onClick={() => setConfirmDelete(true)}
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.error.main, 0.08),
                    },
                  }}
                >
                  Delete
                </Button>
                
                <Button
                  onClick={handleSave}
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={!isNameValid || !hasChanges || loading}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    background: hasChanges 
                      ? `linear-gradient(135deg, ${themeColor.main}, ${themeColor.dark})`
                      : theme.palette.action.disabledBackground,
                    '&:hover': {
                      background: hasChanges 
                        ? `linear-gradient(135deg, ${themeColor.dark}, ${themeColor.main})`
                        : theme.palette.action.disabledBackground,
                      transform: hasChanges ? 'translateY(-1px)' : 'none',
                    },
                    '&:disabled': {
                      color: theme.palette.action.disabled,
                    },
                  }}
                >
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => setConfirmDelete(false)}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    fontWeight: 500,
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      borderColor: theme.palette.text.primary,
                      backgroundColor: alpha(theme.palette.text.primary, 0.04),
                    },
                  }}
                >
                  Cancel
                </Button>
                
                <Button
                  onClick={handleConfirmDelete}
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    '&:hover': {
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  {loading ? "Deleting..." : "Confirm Delete"}
                </Button>
              </>
            )}
          </DialogActions>
        </Box>
      </Fade>
    </Dialog>
  );
};

export default CategoryEditDialogComponent;
